import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CustomerPortalWidgetType } from 'api/customer-portal/types';
import { Loading } from 'components/atoms/loading';
import { queueMacrotask } from 'utils/macrotask';
import { useGetCpRenderInput } from '../hooks/use-get-cp-widget-data';
import { CpWidgetProps } from '../types/widget';
import { CpAllFiles } from '../widgets/all-files/cp-all-files';
import { CpChatWidget } from '../widgets/chat/cp-chat';
import { CpSectionWrapper } from '../widgets/common';
import { CpAccountFiles } from '../widgets/cp-account-files/cp-account-files';
import { CpAccountProjectsList } from '../widgets/cp-account-projects-list/cp-account-project-list';
import { CpAccountSpaces } from '../widgets/cp-account-spaces/cp-account-spaces';
import { CpProjectHours } from '../widgets/cp-project-hours/cp-project-hours';
import { CpProjectOverView } from '../widgets/cp-project-overview';
import { CpProjectPlan } from '../widgets/cp-project-plan';
import { CpSummaryInformation } from '../widgets/cp-summary-information/cp-summary-information';
import { CpCTAWidget } from '../widgets/cta/cp-cta';
import { CpFaqWidget } from '../widgets/faq/cp-faq';
import { CpFeatureWidget } from '../widgets/feature/cp-feature';
import { CpFooter } from '../widgets/footer/cp-footer';
import { CpHeroSection } from '../widgets/hero-section/cp-hero-section';
import { CpImageGalleryWidget } from '../widgets/image-gallery/cp-image-gallery';
import { CpMetricsWidget } from '../widgets/metrics/cp-metrics';
import { CpProjectForms } from '../widgets/project-forms/cp-project-forms';
import { CpProjectUpdates } from '../widgets/project-updates/cp-project-updates';
import { CpResources } from '../widgets/resources/cp-resources';
import { CpSpaces } from '../widgets/space/cp-space';
import { CpVideoListWidget } from '../widgets/video-list/cp-video-list';

const LoadingWidgetWrapper = styled.div`
  width: 100%;
  height: 15rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--cp-white);
`;

export type CpWidgetRendererProps = Omit<CpWidgetProps, 'data'> & {
  defaultData?: boolean;
};

export const CpWidgetRenderer: React.FC<CpWidgetRendererProps> = ({
  projectId,
  widget,
  disabled,
  defaultData,
}) => {
  const [runtimeConfig, setRuntimeConfig] = React.useState({});

  const { data: _data, refetch } = useGetCpRenderInput({
    widgetDefinition: widget?.widgetDefinition,
    widgetType: widget?.widgetType,
    enabled: !disabled,
    projectId,
    defaultData,
    runtimeConfig,
    widgetVariantType: widget?.widgetVariantType,
  });

  const [data, setData] = useState(_data);

  useEffect(() => {
    if (_data) {
      setData(_data);
    }
  }, [_data]);

  useEffect(() => {
    refetch();
  }, [refetch, widget.widgetType]);

  if (!data) {
    return (
      <LoadingWidgetWrapper>
        <Loading size="large" />
      </LoadingWidgetWrapper>
    );
  }

  switch (widget?.widgetType) {
    case CustomerPortalWidgetType.ProjectPlan:
      return (
        <CpProjectPlan
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={(change) => {
            setRuntimeConfig(change);
            queueMacrotask(() => refetch());
          }}
          runtimeConfig={runtimeConfig}
        />
      );

    case CustomerPortalWidgetType.FAQ:
      return (
        <CpFaqWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
        />
      );

    case CustomerPortalWidgetType.Resources:
      return (
        <CpResources
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
        />
      );

    case CustomerPortalWidgetType.HeroSection:
      return (
        <CpHeroSection
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
        />
      );

    case CustomerPortalWidgetType.AccountProjectsList:
      return (
        <CpAccountProjectsList
          widget={widget}
          data={data}
          projectId={projectId}
          disabled={disabled}
        />
      );

    case CustomerPortalWidgetType.ProjectSummaryInformation:
    case CustomerPortalWidgetType.AccountSummaryInformation:
      return (
        <CpSummaryInformation
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
        />
      );

    case CustomerPortalWidgetType.CHATS:
      return (
        <CpChatWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.AllFiles:
      return (
        <CpAllFiles
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.AccountFiles:
      return (
        <CpAccountFiles
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.SPACE:
      return (
        <CpSpaces
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.AccountSpaces:
      return (
        <CpAccountSpaces
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.ProjectUpdates:
      return (
        <CpProjectUpdates
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.ImageGallery:
      return (
        <CpImageGalleryWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.Metrics:
      return (
        <CpMetricsWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.VideoList:
      return (
        <CpVideoListWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.CTA:
      return (
        <CpCTAWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.Feature:
      return (
        <CpFeatureWidget
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );
    case CustomerPortalWidgetType.Footer:
      return (
        <CpFooter
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.ProjectForms:
      return (
        <CpProjectForms
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.ProjectOverview:
      return (
        <CpProjectOverView
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    case CustomerPortalWidgetType.ProjectHours:
      return (
        <CpProjectHours
          widget={widget}
          projectId={projectId}
          disabled={disabled}
          data={data}
          onRuntimeConfigChange={refetch}
        />
      );

    default:
      return <CpSectionWrapper />;
  }
};
